/**
 * Adjuntos (fotos, videos y documentos) en las tablas y formularios de /finanzas-empresa.
 *
 * Regla de datos: una grilla o una columna de 36-80 px pinta la MINIATURA (`urlMiniatura`),
 * nunca la foto completa; el visor, la vista ampliada y las descargas usan la `url` completa; y
 * un video se reproduce con `<VideoPlayer>` (480p por defecto cuando existe `previewUrl`).
 *
 * El tipo (`kind`) viene del archivo, NO de la extensión de la URL: las URLs firmadas
 * (`/files/<token>`) no la llevan.
 */
import { useRef, useState } from "react";
import type { CarouselRef } from "antd/es/carousel";
import type { CSSProperties, ReactNode } from "react";
import { Carousel, Image, Modal, Tag } from "antd";
import { FileOutlined, PlayCircleFilled } from "@ant-design/icons";
import VideoPlayer from "../../Components/VideoPlayer";
import VisorImagen from "../../Components/VisorImagen";
import { resolveFileUrl } from "../../utils/safeUrl";
import { urlMiniatura } from "../../utils/miniatura";

export type TipoAdjunto = "image" | "video" | "doc";

export interface Adjunto {
  /** URL del archivo COMPLETO (visores, descargas, reproducción en HD). */
  url: string;
  /** Miniatura (en un video, un fotograma). `null`: se cae a la URL completa o a un ícono. */
  thumbUrl: string | null;
  /** Versión liviana (480p) de un video; `null` si no existe. */
  previewUrl: string | null;
  kind: TipoAdjunto;
  originalName: string | null;
  /** key de storage del entry, si la respuesta la trae (sirve para borrar sin depender de la firma). */
  key: string | null;
}

const VIDEO_RE = /\.(mp4|webm|mov|m4v)(\?|#|$)/i;

/** URL saneada (solo http/https o relativa al backend); `null` si queda inválida. */
function urlSegura(u: unknown): string | null {
  if (typeof u !== "string" || !u) return null;
  const r = resolveFileUrl(u);
  return r === "about:blank" ? null : r;
}

function tipoDe(x: any, url: string): TipoAdjunto {
  if (x && typeof x === "object") {
    if (x.kind === "image" || x.kind === "video" || x.kind === "doc") return x.kind;
    const mime = typeof x.mimeType === "string" ? x.mimeType : "";
    if (mime.startsWith("video/")) return "video";
    if (mime.startsWith("image/")) return "image";
    if (mime) return "doc";
  }
  return VIDEO_RE.test(url) ? "video" : "image";
}

/**
 * Lista de adjuntos desde lo que mande la API: entries completos (`{ url, thumbUrl,
 * previewUrl, kind, ... }`) o strings legacy (solo la URL). Descarta lo que no tenga una URL
 * segura.
 */
export function normalizarAdjuntos(lista: unknown): Adjunto[] {
  if (!Array.isArray(lista)) return [];
  const out: Adjunto[] = [];
  for (const x of lista) {
    const url = urlSegura(typeof x === "string" ? x : x?.url);
    if (!url) continue;
    const e = typeof x === "string" ? null : x;
    out.push({
      url,
      thumbUrl: urlSegura(e?.thumbUrl),
      previewUrl: urlSegura(e?.previewUrl),
      kind: tipoDe(x, url),
      originalName: typeof e?.originalName === "string" ? e.originalName : null,
      key: typeof e?.key === "string" ? e.key : null,
    });
  }
  return out;
}

/**
 * Adjuntos de un registro de Productos: prefiere `imagenesDetalle` (con miniatura y versión
 * liviana); si el backend solo mandó `imagenes` (URLs planas), cae a esa lista.
 */
export function adjuntosDeRegistro(imagenes: unknown, imagenesDetalle: unknown): Adjunto[] {
  const detalle = normalizarAdjuntos(imagenesDetalle);
  return detalle.length ? detalle : normalizarAdjuntos(imagenes);
}

/** Miniatura de un adjunto: foto reducida; video con fotograma y ▶ (o ícono); documento con ícono. */
export function MiniaturaAdjunto({
  adjunto,
  size = 36,
  onClick,
  testId,
}: {
  adjunto: Adjunto;
  size?: number;
  onClick?: () => void;
  testId?: string;
}) {
  const caja: CSSProperties = {
    width: size,
    height: size,
    borderRadius: size >= 60 ? 4 : 6,
    border: "1px solid #eee",
    objectFit: "cover",
    cursor: "pointer",
    display: "inline-flex",
  };
  const titulo = adjunto.originalName ?? undefined;

  // Documento, o video sin fotograma: ícono.
  const sinImagen = adjunto.kind === "doc" || (adjunto.kind === "video" && !adjunto.thumbUrl);
  if (sinImagen) {
    return (
      <span
        role={onClick ? "button" : undefined}
        title={titulo}
        data-testid={testId}
        onClick={onClick}
        style={{
          ...caja,
          alignItems: "center",
          justifyContent: "center",
          background: "#f5f5f5",
          fontSize: Math.round(size * 0.5),
          color: "#555",
        }}
      >
        {adjunto.kind === "video" ? <PlayCircleFilled /> : <FileOutlined />}
      </span>
    );
  }

  const img = (
    <img
      className="thumb"
      src={urlMiniatura(adjunto)}
      alt={adjunto.originalName ?? "Adjunto"}
      loading="lazy"
      data-testid={testId}
      onClick={adjunto.kind === "video" ? undefined : onClick}
      style={{ ...caja, display: "block" }}
    />
  );
  if (adjunto.kind !== "video") return img;
  // Video con fotograma: se marca con un ▶ encima para distinguirlo de una foto.
  return (
    <span
      role={onClick ? "button" : undefined}
      title={titulo}
      onClick={onClick}
      style={{ position: "relative", display: "inline-flex", cursor: "pointer" }}
    >
      {img}
      <PlayCircleFilled
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          margin: "auto",
          fontSize: Math.round(size * 0.45),
          color: "#fff",
          textShadow: "0 0 4px rgba(0,0,0,.6)",
          pointerEvents: "none",
        }}
      />
    </span>
  );
}

/** Reproduce un video en un modal: abre con la versión liviana si existe (selector Baja/HD). */
export function VisorVideoModal({
  adjunto,
  onClose,
}: {
  adjunto: Adjunto | null;
  onClose: () => void;
}) {
  return (
    <Modal
      open={!!adjunto}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={720}
      style={{ top: 24 }}
      title={adjunto?.originalName || "Video"}
    >
      {adjunto && (
        <VideoPlayer
          src={adjunto.url}
          previewSrc={adjunto.previewUrl}
          nombreArchivo={adjunto.originalName ?? undefined}
          altoMaximo="calc(100dvh - 220px)"
          testId="adjunto-video"
        />
      )}
    </Modal>
  );
}

/**
 * Un adjunto como tile: la foto reducida con vista ampliada de la foto COMPLETA (dentro de un
 * `Image.PreviewGroup` se navega entre las fotos del grupo), el video que abre `onAbrirVideo`, y
 * el documento como enlace a su archivo.
 */
export function TileAdjunto({
  adjunto,
  size,
  onAbrirVideo,
  mascara = false,
}: {
  adjunto: Adjunto;
  size: number;
  onAbrirVideo: (a: Adjunto) => void;
  /** Texto de la máscara al pasar el mouse sobre una foto (false = sin máscara). */
  mascara?: ReactNode;
}) {
  if (adjunto.kind === "image") {
    return (
      <Image
        className="thumb"
        src={urlMiniatura(adjunto)}
        alt={adjunto.originalName ?? "Adjunto"}
        width={size}
        height={size}
        style={{ objectFit: "cover", borderRadius: size >= 60 ? 4 : 6, cursor: "pointer" }}
        preview={{ src: adjunto.url, mask: mascara, zIndex: 2000 }}
      />
    );
  }
  if (adjunto.kind === "video") {
    return <MiniaturaAdjunto adjunto={adjunto} size={size} onClick={() => onAbrirVideo(adjunto)} />;
  }
  return (
    <a href={adjunto.url} target="_blank" rel="noopener noreferrer" title="Abrir documento">
      <MiniaturaAdjunto adjunto={adjunto} size={size} />
    </a>
  );
}

/**
 * Celda de tabla con los adjuntos de una fila: hasta `max` tiles (miniaturas), `+N` con el
 * resto y, si hay fotos ocultas, siguen navegables en la vista ampliada.
 */
export function AdjuntosCelda({
  adjuntos,
  max = 3,
  size = 36,
}: {
  adjuntos: Adjunto[];
  max?: number;
  size?: number;
}) {
  const [video, setVideo] = useState<Adjunto | null>(null);
  if (!adjuntos.length) return <>—</>;
  const visibles = adjuntos.slice(0, max);
  const resto = adjuntos.slice(max);
  return (
    <>
      <Image.PreviewGroup>
        <div className="thumbs">
          {visibles.map((a, i) => (
            <TileAdjunto key={`${a.url}-${i}`} adjunto={a} size={size} onAbrirVideo={setVideo} />
          ))}
          {/* Fotos que no caben en la celda: montadas sin verse, para seguir navegables. */}
          <div style={{ display: "none" }}>
            {resto
              .filter((a) => a.kind === "image")
              .map((a, i) => (
                <Image
                  key={`${a.url}-${i}`}
                  src={urlMiniatura(a)}
                  alt={a.originalName ?? "Adjunto"}
                  preview={{ src: a.url }}
                />
              ))}
          </div>
          {resto.length > 0 ? <Tag>+{resto.length}</Tag> : null}
        </div>
      </Image.PreviewGroup>
      <VisorVideoModal adjunto={video} onClose={() => setVideo(null)} />
    </>
  );
}

/**
 * Carrusel a tamaño completo (modal de Productos): la foto COMPLETA, el video con
 * `<VideoPlayer>` y su versión liviana (selector Baja/HD) y el documento como enlace.
 */
export function CarruselAdjuntos({
  adjuntos,
  actual,
  onCambio,
  vacio = "No hay archivos.",
}: {
  adjuntos: Adjunto[];
  /** Índice del slide visible (para el contador «n / total»). */
  actual: number;
  onCambio: (indice: number) => void;
  vacio?: string;
}) {
  const carrusel = useRef<CarouselRef>(null);
  if (!adjuntos.length) {
    return <div style={{ textAlign: "center", padding: 24, color: "#999" }}>{vacio}</div>;
  }
  return (
    <Carousel ref={carrusel} arrows dots infinite={adjuntos.length > 1} afterChange={(idx) => onCambio(idx)}>
      {adjuntos.map((adj, i) => (
        <div key={`${adj.url}-${i}`}>
          <div
            style={{
              position: "relative",
              height: "60vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#111",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {adj.kind === "video" ? (
                <VideoPlayer
                  src={adj.url}
                  previewSrc={adj.previewUrl}
                  nombreArchivo={adj.originalName ?? undefined}
                  ajuste="llenar"
                />
              ) : adj.kind === "doc" ? (
                <a
                  href={adj.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#fff" }}
                >
                  {adj.originalName || "Abrir documento"}
                </a>
              ) : (
                <VisorImagen src={adj.url} alt={adj.originalName || `Imagen ${i + 1}`} altura="calc(60vh - 52px)" onSwipe={(paso) => paso > 0 ? carrusel.current?.next() : carrusel.current?.prev()} />
              )}
            </div>
            <div
              style={{
                position: "absolute",
                left: 12,
                top: 12,
                zIndex: 9,
                background: "rgba(0,0,0,.55)",
                color: "#fff",
                padding: "2px 8px",
                borderRadius: 12,
                fontSize: 12,
              }}
            >
              {actual + 1} / {adjuntos.length}
            </div>
          </div>
        </div>
      ))}
    </Carousel>
  );
}
